// src/components/UserDataForm.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader, AlertCircle, User, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';

const Button = ({ onClick, children, className, disabled, type = "button" }) => (
    <button onClick={onClick} className={className} disabled={disabled} type={type}>
        {children}
    </button>
);

const Input = ({ label, icon: Icon, error, ...props }) => (
    <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
        </label>
        <div className="relative">
            {Icon && (
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon className="h-5 w-5 text-gray-400" />
                </div>
            )}
            <input
                {...props}
                className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${error ? 'border-red-500' : ''}`}
            />
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
);

export default function UserDataForm() {
    const { id: serviceId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const hasLoaded = useRef(false);

    const [service, setService] = useState(location.state?.service || null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: 'Italy',
        dateOfBirth: ''
    });

    useEffect(() => {
        if (hasLoaded.current) return;
        hasLoaded.current = true;
        const consent = localStorage.getItem('cookieConsent');
        let token;

        if (consent === 'true') {
            token = localStorage.getItem('jwtToken');
        } else {
            token = sessionStorage.getItem('jwtToken');
        }
        if (!token) {
            toast.error("Per favore accedi per visualizzare questa pagina.");
            navigate('/login', { state: { from: location.pathname }, replace: true });
            return;
        }

        const loadData = async () => {
            setIsLoading(true);
            setError('');

            try {
                // Load service data if not available from location state
                if (!location.state?.service?.title || !location.state?.service?.price) {
                    console.log('Fetching service data...');
                    const serviceRes = await axios.get(`https://api.cafpraticheonline.it/api/services/${serviceId}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    setService({
                        id: serviceRes.data.service_id,
                        title: serviceRes.data.service_name,
                        price: serviceRes.data.price
                    });
                }

                // Fetch user profile data to pre-fill the form (for convenience only)
                console.log('Loading existing profile data for convenience...');
                try {
                    const userRes = await axios.get('https://api.cafpraticheonline.it/api/user/profile', {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });

                    if (userRes.data) {
                        console.log('User data loaded for pre-filling:', userRes.data);
                        const userData = userRes.data;


                        // Format date properly for input field
                        let formattedDate = '';
                        if (userData.dob) {
                            const date = new Date(userData.dob);
                            if (!isNaN(date.getTime())) {
                                formattedDate = date.toISOString().split('T')[0];
                            }
                        }

                        let firstName = userData.first_name || '';
                        let lastName = userData.last_name || '';

                        // Fallback to parsing 'name' field if first/last names don't exist
                        if ((!firstName || !lastName) && userData.name) {
                            const nameParts = userData.name.split(' ');
                            firstName = nameParts[0] || '';
                            lastName = nameParts.slice(1).join(' ') || '';
                        }

                        setFormData({
                            firstName: firstName,
                            lastName: lastName,
                            email: userData.email || '',
                            phone: userData.phone || '',
                            address: userData.address || '',
                            city: userData.city || '',
                            country: userData.country || 'Italy',
                            dateOfBirth: formattedDate
                        });

                        toast.success('Dati del profilo caricati per tua comodità!');
                    } else {
                        toast.info('Per favore inserisci le tue informazioni per questo servizio.');
                    }
                } catch (userErr) {
                    console.log('No existing profile data found:', userErr);
                    toast.info('Per favore inserisci le tue informazioni per questo servizio.');
                }

            } catch (err) {
                console.error('Error loading service data:', err);
                setError('Impossibile caricare i dati del servizio.');
                toast.error('Impossibile caricare i dati del servizio. Per favore riprova.');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [serviceId, location.pathname, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = 'Il nome è obbligatorio';
        if (!formData.lastName.trim()) newErrors.lastName = 'Il cognome è obbligatorio';
        if (!formData.email.trim()) {
            newErrors.email = 'L’email è obbligatoria';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'L’email non è valida';
        }
        if (!formData.phone.trim()) newErrors.phone = 'Il numero di telefono è obbligatorio';
        if (!formData.address.trim()) newErrors.address = 'L’indirizzo è obbligatorio';
        if (!formData.city.trim()) newErrors.city = 'La città è obbligatoria';
        if (!formData.country.trim()) newErrors.country = 'Il paese è obbligatorio';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'La data di nascita è obbligatoria';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Per favore compila correttamente tutti i campi obbligatori.');
            return;
        }

        setIsSubmitting(true);
        console.log('User data collected for booking process:', formData);

        try {
            // Just validate the data without saving to database
            await new Promise(resolve => setTimeout(resolve, 500));
            toast.success('Informazioni raccolte! Procedo al caricamento dei documenti.');

            // Store the booking data in localStorage temporarily for the entire booking process
            const bookingData = {
                service: service,
                userData: formData,
                timestamp: new Date().toISOString(),
                bookingId: `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            };

            const consent = localStorage.getItem('cookieConsent');

            if (consent === 'true') {
                localStorage.setItem('currentBooking', JSON.stringify(bookingData));
            } else {
                sessionStorage.setItem('currentBooking', JSON.stringify(bookingData));
            }

            // Navigate to document upload page with all collected data
            navigate(`/services/${serviceId}/documents`, {
                state: {
                    service,
                    userData: formData,
                    bookingData: bookingData
                }
            });
        } catch (err) {
            console.error('Error during navigation:', err);
            toast.error('Qualcosa è andato storto. Per favore riprova.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col">
                <Loader className="animate-spin text-yellow-500 h-10 w-10 mb-4" />
                <p className="text-gray-600">Caricamento dei dettagli del servizio e del profilo...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col text-red-600">
                <AlertCircle className="h-10 w-10 mb-4" />
                <p>{error}</p>
                <Button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                >
                    Riprova
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Service Info Header */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">{service?.title}</h1>
                    <p className="text-gray-600 mb-2">
                        Prezzo: <span className="font-semibold text-yellow-600">€{service?.price}</span>
                    </p>
                    <div className="text-xs bg-blue-50 text-blue-700 p-2 rounded-lg">
                        <strong>Nota:</strong>  Le tue informazioni saranno inviate al nostro team amministrativo dopo il completamento del pagamento.
                        Nessun dato verrà salvato finché la prenotazione non sarà finalizzata.

                    </div>
                </div>

                {/* User Data Form */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Informazioni Personali</h2>
                    <p className="text-gray-600 mb-6 text-sm">
                        Per favore fornisci le tue informazioni per la prenotazione di questo servizio.
                        Questi dati saranno inclusi nella tua richiesta inviata al nostro team amministrativo.

                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                                label="Nome"
                                icon={User}
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                error={errors.firstName}
                                required
                            />

                            <Input
                                label="Cognome"
                                icon={User}
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                error={errors.lastName}
                                required
                            />
                        </div>

                        <Input
                            label="Email"
                            icon={Mail}
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={errors.email}
                            required
                        />

                        <Input
                            label="Numero di Telefono"
                            icon={Phone}
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={errors.phone}
                            required
                        />

                        <Input
                            label="Indirizzo"
                            icon={MapPin}
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            error={errors.address}
                            required
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                                label="Città"
                                icon={MapPin}
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                error={errors.city}
                                required
                            />

                            <Input
                                label="Paese"
                                icon={MapPin}
                                type="text"
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                error={errors.country}
                                required
                                disabled
                            />
                        </div>

                        <Input
                            label="Data di Nascita"
                            icon={Calendar}
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleInputChange}
                            error={errors.dateOfBirth}
                            required
                        />

                        <div className="mt-8 flex gap-4">
                            <Button
                                type="button"
                                onClick={() => navigate('/services')}
                                className="flex-1 py-3 rounded-lg font-bold text-lg transition-all duration-200
                                            bg-gray-200 text-gray-700 hover:bg-gray-300"
                            >
                                Indietro
                            </Button>

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 py-3 rounded-lg font-bold text-lg transition-all duration-200
                                            bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]
                                            disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Elaborazione...' : 'Continua ai Documenti'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
            <Toaster />
        </div>
    );
}